import { Icon } from '@iconify/react';
import ReactMarkdown from 'react-markdown';
import ReactQuill from 'react-quill';
import PhoneInput, { formatPhoneNumberIntl } from 'react-phone-number-input';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';
import type {
  DetailTab,
  Student,
  StudentStatus,
  ResourceType,
  SharedResource,
} from './types';

type StudentDetailPanelProps = {
  data: {
    isLoading: boolean;
    hasDetailError: boolean;
    student: Student | null;
    detailTabs: ReadonlyArray<DetailTab>;
    activeTab: DetailTab;
    openStatusId: string | null;
    statusUpdating: boolean;
    updatingStatusId: string | null;
    statusStyles: Record<StudentStatus, string>;
    statusOptions: ReadonlyArray<{
      value: StudentStatus;
      label: string;
      text: string;
    }>;
    isEditingEmail: boolean;
    emailDraft: string;
    displayEmail: string;
    isEditingPhone: boolean;
    phoneDraft: string;
    displayPhone: string;
    contactUpdating: boolean;
    showNoteEditor: boolean;
    noteDraft: string;
    noteModules: Record<string, unknown>;
    noteFormats: string[];
    isPostDisabled: boolean;
    notePosting: boolean;
    resourceTypeStyles: Record<ResourceType, { badge: string; icon: string }>;
    formatResourceDate: (value?: string) => string;
  };
  actions: {
    onBack: () => void;
    onToggleStatusMenu: (id: string | null) => void;
    onStatusSelect: (student: Student, status: StudentStatus) => void;
    onTabChange: (tab: DetailTab) => void;
    onEmailDraftChange: (value: string) => void;
    onEmailSave: () => void;
    onEmailCancel: () => void;
    onEmailAdd: () => void;
    onPhoneDraftChange: (value: string) => void;
    onPhoneSave: () => void;
    onPhoneCancel: () => void;
    onPhoneAdd: () => void;
    onNoteDraftChange: (value: string) => void;
    onToggleNoteEditor: (open: boolean) => void;
    onPostNote: () => void;
    onOpenResource: (resource: SharedResource) => void;
    onOpenMessages: (studentId: string) => void;
  };
};

const formatDisplayPhone = (value: string) => {
  if (!value) {
    return '';
  }
  try {
    return formatPhoneNumberIntl(value);
  } catch {
    return value;
  }
};

const StudentDetailPanel = ({ data, actions }: StudentDetailPanelProps) => {
  const {
    isLoading,
    hasDetailError,
    student,
    detailTabs,
    activeTab,
    openStatusId,
    statusUpdating,
    updatingStatusId,
    statusStyles,
    statusOptions,
    isEditingEmail,
    emailDraft,
    displayEmail,
    isEditingPhone,
    phoneDraft,
    displayPhone,
    contactUpdating,
    showNoteEditor,
    noteDraft,
    noteModules,
    noteFormats,
    isPostDisabled,
    notePosting,
    resourceTypeStyles,
    formatResourceDate,
  } = data;
  const {
    onBack,
    onToggleStatusMenu,
    onStatusSelect,
    onTabChange,
    onEmailDraftChange,
    onEmailSave,
    onEmailCancel,
    onEmailAdd,
    onPhoneDraftChange,
    onPhoneSave,
    onPhoneCancel,
    onPhoneAdd,
    onNoteDraftChange,
    onToggleNoteEditor,
    onPostNote,
    onOpenResource,
    onOpenMessages,
  } = actions;

  if (isLoading) {
    return (
      <div className="flex min-w-0 flex-1 flex-col gap-6 rounded-[12px] border border-slate-200 bg-white px-6 py-6">
        <div className="flex flex-col gap-6 border-b border-slate-100 pb-6">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="h-14 w-14 rounded-full bg-slate-100" />
              <div className="space-y-2">
                <div className="h-4 w-32 rounded-full bg-slate-100" />
                <div className="h-3 w-24 rounded-full bg-slate-50" />
              </div>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-7 w-20 rounded-full bg-slate-100" />
              <div className="h-9 w-24 rounded-xl bg-slate-100" />
            </div>
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="h-20 rounded-[16px] bg-slate-50" />
            <div className="h-20 rounded-[16px] bg-slate-50" />
          </div>
        </div>
        <div className="flex flex-wrap gap-4 border-b border-slate-100 pb-3">
          {detailTabs.map((tab) => (
            <div
              key={`skeleton-${tab}`}
              className="h-4 w-20 rounded-full bg-slate-100"
            />
          ))}
        </div>
        <div className="space-y-3">
          <div className="h-4 w-full rounded-full bg-slate-50" />
          <div className="h-4 w-4/5 rounded-full bg-slate-50" />
          <div className="h-4 w-2/3 rounded-full bg-slate-50" />
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="flex min-w-0 flex-1 flex-col items-center justify-center gap-2 rounded-[12px] border border-slate-200 bg-white px-6 py-6 text-sm text-slate-500">
        {hasDetailError ? 'Student not found.' : null}
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-1 flex-col gap-6 rounded-[12px] border border-slate-200 bg-white px-6 py-6">
      <div className="flex flex-col gap-6 border-b border-slate-100 pb-6">
        <button
          type="button"
          className="flex items-center gap-1 text-xs font-semibold hover:text-slate-700"
          onClick={onBack}
        >
          <Icon icon="mingcute:align-arrow-right-line" width={20} height={20} />
        </button>

        <div className="flex flex-wrap -mt-2 items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-slate-100 text-sm font-semibold text-slate-600">
              {student.initials}
            </div>
            <div>
              <p className="text-lg font-semibold text-slate-900">
                {student.name}
              </p>
              <p className="text-xs text-slate-400">
                Last session {student.lastSession}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {student.linkedIn ? (
              <button
                className="flex h-9 w-9 items-center justify-center text-slate-600 transition hover:border-slate-300 hover:text-slate-900"
                type="button"
                aria-label="Open LinkedIn"
              >
               <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
               <path d="M20.4491 20.4495H16.8931V14.8804C16.8931 13.5525 16.8694 11.8429 15.0436 11.8429C13.1915 11.8429 12.9081 13.2899 12.9081 14.7838V20.4491H9.35213V8.99697H12.7658V10.562H12.8137C13.1553 9.97789 13.649 9.49736 14.2421 9.17158C14.8353 8.84581 15.5057 8.687 16.1819 8.71206C19.7861 8.71206 20.4506 11.0828 20.4506 14.167L20.4491 20.4495ZM5.33963 7.43162C4.19991 7.43181 3.27581 6.508 3.27562 5.36828C3.27544 4.22856 4.19916 3.30447 5.33887 3.30428C6.47859 3.304 7.40269 4.22781 7.40287 5.36753C7.40297 5.91484 7.18566 6.43978 6.79872 6.82687C6.41179 7.21396 5.88694 7.4315 5.33963 7.43162ZM7.11769 20.4496H3.55791V8.99697H7.11759L7.11769 20.4496ZM22.2219 0.00174674H1.77103C0.804469 -0.00912826 0.0118125 0.765153 0 1.73172V22.2679C0.0114375 23.235 0.804 24.01 1.77094 23.9999H22.2219C23.1909 24.0119 23.9866 23.2368 24 22.2679V1.73012C23.9862 0.761684 23.1905 -0.0125033 22.2219 0.00015299" fill="#0A66C2"/>
               </svg>
              </button>
            ) : null}
            <div className="relative">
              <button
                type="button"
                className={`rounded-[8px] px-3 py-2 text-xs font-semibold ${statusStyles[student.status]}`}
                onClick={(event) => {
                  event.stopPropagation();
                  const key = `detail-${student.id}`;
                  onToggleStatusMenu(openStatusId === key ? null : key);
                }}
                aria-haspopup="menu"
                aria-expanded={openStatusId === `detail-${student.id}`}
                disabled={statusUpdating}
              >
                {student.status}
              </button>
              {openStatusId === `detail-${student.id}` ? (
                <div
                  className="absolute right-0 top-full z-20 mt-2 w-44 rounded-2xl border border-slate-100 bg-white p-2 shadow-lg"
                  role="menu"
                  onClick={(event) => event.stopPropagation()}
                >
                  {statusOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      className={`w-full rounded-xl px-3 py-2 text-left text-base font-semibold ${option.text} hover:bg-slate-50`}
                      onClick={() => onStatusSelect(student, option.value)}
                      disabled={updatingStatusId === student.id}
                      role="menuitem"
                    >
                      
                      {option.label}
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            <button
              className="rounded-[10px] flex text-center gap-1 border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
              type="button"
              onClick={() => onOpenMessages(student.id)}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
               <g clip-path="url(#clip0_161_17683)">
                <path d="M15.8332 2.5C16.4962 2.5 17.1321 2.76339 17.6009 3.23223C18.0698 3.70107 18.3332 4.33696 18.3332 5V13.3333C18.3332 13.9964 18.0698 14.6323 17.6009 15.1011C17.1321 15.5699 16.4962 15.8333 15.8332 15.8333H6.11067L3.33317 17.9167C2.6465 18.4317 1.6665 17.9417 1.6665 17.0833V5C1.6665 4.33696 1.9299 3.70107 2.39874 3.23223C2.86758 2.76339 3.50346 2.5 4.1665 2.5H15.8332ZM15.8332 4.16667H4.1665C3.94549 4.16667 3.73353 4.25446 3.57725 4.41074C3.42097 4.56702 3.33317 4.77899 3.33317 5V15.8333L5.11067 14.5C5.39916 14.2836 5.75005 14.1667 6.11067 14.1667H15.8332C16.0542 14.1667 16.2661 14.0789 16.4224 13.9226C16.5787 13.7663 16.6665 13.5543 16.6665 13.3333V5C16.6665 4.77899 16.5787 4.56702 16.4224 4.41074C16.2661 4.25446 16.0542 4.16667 15.8332 4.16667ZM9.1665 10C9.3789 10.0002 9.5832 10.0816 9.73764 10.2274C9.89209 10.3732 9.98503 10.5725 9.99748 10.7845C10.0099 10.9965 9.94094 11.2053 9.80462 11.3682C9.66829 11.5311 9.47492 11.6357 9.264 11.6608L9.1665 11.6667H6.6665C6.4541 11.6664 6.24981 11.5851 6.09536 11.4393C5.94092 11.2935 5.84797 11.0942 5.83553 10.8822C5.82308 10.6701 5.89207 10.4614 6.02839 10.2985C6.16472 10.1356 6.35809 10.0309 6.569 10.0058L6.6665 10H9.1665ZM13.3332 6.66667C13.5542 6.66667 13.7661 6.75446 13.9224 6.91074C14.0787 7.06703 14.1665 7.27899 14.1665 7.5C14.1665 7.72101 14.0787 7.93298 13.9224 8.08926C13.7661 8.24554 13.5542 8.33333 13.3332 8.33333H6.6665C6.44549 8.33333 6.23353 8.24554 6.07725 8.08926C5.92097 7.93298 5.83317 7.72101 5.83317 7.5C5.83317 7.27899 5.92097 7.06703 6.07725 6.91074C6.23353 6.75446 6.44549 6.66667 6.6665 6.66667H13.3332Z" fill="#0F0F0F"/>
                 </g>
                <defs>
                 <clipPath id="clip0_161_17683">
                <rect width="20" height="20" fill="white"/>
               </clipPath>
               </defs>
                </svg>
              Message
            </button>
          </div>
        </div>
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="rounded-[20px] border border-slate-100 bg-white px-4 py-4 shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-600">
               <Icon icon="mingcute:phone-line" width={20} height={20} />
            </div>
            <div className="mt-4">
              <p className="text-3xl font-semibold mb-2 text-slate-900">
                {student.sessionsAttended}
              </p>
              <p className="text-sm font-semibold text-emerald-600">
                Session Attended
              </p>
            </div>
          </div>
          <div className="rounded-[20px] border border-slate-100 bg-white px-4 py-4 shadow-sm">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-rose-100 text-rose-600">
               <Icon icon="mingcute:forbid-circle-line" width={20} height={20} />
            </div>
            <div className="mt-4">
              <p className="text-3xl font-semibold mb-2 text-slate-900">
                {student.sessionsMissed}
              </p>
              <p className="text-sm font-semibold text-rose-600">
                Session Missed
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-10 border-b border-slate-100 text-sm font-semibold">
        {detailTabs.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => onTabChange(tab)}
            className={`pb-3 transition ${
              activeTab === tab
                ? 'border-b-2 font-bold border-slate-900 text-slate-900'
                : 'text-slate-400 hover:text-slate-600'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      <div className="flex flex-col gap-6 text-sm text-slate-600">
        {activeTab === 'About' ? (
          <>
            <p className='font-[14px] font-medium'>{student.about}</p>
            <div className="grid gap-4">
              <div className="grid gap-2 sm:grid-cols-[140px_1fr]">
                <span className="text-[#9FA8BB] font-[14px] font-semibold">Focus Area</span>
                <span className="text-[#0F0F0F] font-[14px] font-semibold">
                  {student.focusArea}
                </span>
              </div>
              <div className="grid gap-2 sm:grid-cols-[140px_1fr]">
                <span className="text-[#9FA8BB] font-[14px] font-semibold">Goals</span>
                <div className="space-y-1 text-slate-700">
                  {student.goals.map((goal) => (
                    <p  className="text-[#0F0F0F] font-[14px] font-semibold" key={goal}>{goal}</p>
                  ))}
                </div>
              </div>
              <div className="grid gap-2 sm:grid-cols-[140px_1fr]">
                <span className="text-[#9FA8BB] font-[14px] font-semibold">Email</span>
                {isEditingEmail ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex min-w-[240px] items-center gap-3 rounded-[12px] border border-slate-200 bg-white px-4 py-2 text-slate-700">
                      <input
                        type="email"
                        value={emailDraft}
                        onChange={(event) =>
                          onEmailDraftChange(event.target.value)
                        }
                        placeholder="sara@example.com"
                        className="w-full bg-transparent text-sm font-semibold text-slate-900 outline-none"
                      />
                    </div>
                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-blue-600 text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-70"
                      aria-label="Save email"
                      onClick={onEmailSave}
                      disabled={contactUpdating || !emailDraft.trim()}
                    >
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="m5 12 4 4L19 6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-[12px] border border-slate-200 text-slate-500 transition hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Cancel email"
                      onClick={onEmailCancel}
                      disabled={contactUpdating}
                    >
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>
                ) : displayEmail ? (
                  <span className="text-sm font-semibold text-slate-900">
                    {displayEmail}
                  </span>
                ) : (
                  <div className="flex flex-col gap-1">
                    <span className="text-xl font-semibold text-slate-900">
                      No Email Address
                    </span>
                    <button
                      className="w-fit text-base font-semibold text-blue-600 underline"
                      type="button"
                      onClick={onEmailAdd}
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>
              <div className="grid gap-2 sm:grid-cols-[140px_1fr]">
                <span className="text-[#9FA8BB] font-[14px] font-semibold">Contact</span>
                {isEditingPhone ? (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex min-w-[260px] items-center rounded-[12px] border border-slate-200 bg-white px-4 py-2 text-slate-700">
                      <PhoneInput
                        international
                        defaultCountry="US"
                        value={phoneDraft}
                        onChange={(value) =>
                          onPhoneDraftChange(value ?? '')
                        }
                        placeholder="+1 (505) 555-0125"
                        className="phone-input w-full"
                      />
                    </div>
                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-[12px] bg-blue-600 text-white shadow-sm disabled:cursor-not-allowed disabled:opacity-70"
                      aria-label="Save contact"
                      onClick={onPhoneSave}
                      disabled={contactUpdating || !phoneDraft.trim()}
                    >
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="m5 12 4 4L19 6" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      className="flex h-10 w-10 items-center justify-center rounded-[12px] border border-slate-200 text-slate-500 transition hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60"
                      aria-label="Cancel contact"
                      onClick={onPhoneCancel}
                      disabled={contactUpdating}
                    >
                      <svg
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <path d="M18 6 6 18" />
                        <path d="m6 6 12 12" />
                      </svg>
                    </button>
                  </div>
                ) : displayPhone ? (
                  <span className="text-sm font-semibold text-slate-900">
                    {formatDisplayPhone(displayPhone)}
                  </span>
                ) : (
                  <div className="flex flex-col gap-1">
                    <span className="text-xl font-semibold text-slate-900">
                      No Contact Number
                    </span>
                    <button
                      className="w-fit text-base font-semibold text-blue-600 underline"
                      type="button"
                      onClick={onPhoneAdd}
                    >
                      Add
                    </button>
                  </div>
                )}
              </div>
            </div>
          </>
        ) : null}
        {activeTab === 'Last Session' ? (
          <div className="markdown-data text-[#0F0F0F] font-[14px] font-semibold">
            <p className='font-[14px] font-semibold text-[#0F0F0F]' style={{marginBottom:"10px"}}>Session Summary</p>
            <div className='font-[14px] font-medium text-[#0F0F0F]'>
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>
              {student.lastSessionNotes}
            </ReactMarkdown>
            </div>

          </div>
        ) : null}
        {activeTab === 'Notes' ? (
          <div className="flex flex-col gap-4">
            {!showNoteEditor ? (
              <button
                type="button"
                className="w-fit rounded-[12px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
                onClick={() => onToggleNoteEditor(true)}
              >
                <span className="mr-2 text-lg leading-none">+</span>
                Add
              </button>
            ) : null}
            {!showNoteEditor && student.notes.length === 0 ? (
              <p>No notes yet.</p>
            ) : null}
            {showNoteEditor ? (
              <div className="rounded-[18px] border border-slate-100 bg-white p-4 shadow-sm">
                <div className="rounded-[16px] bg-slate-50">
                  <ReactQuill
                    theme="snow"
                    value={noteDraft}
                    onChange={onNoteDraftChange}
                    placeholder="Write your note here"
                    modules={noteModules}
                    formats={noteFormats}
                    className="note-editor min-h-[11rem]"
                  />
                </div>
                <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                  <div
                    id="note-toolbar"
                    className="flex items-center gap-2 bg-[rgba(228,231,236,0.2)] rounded-[4px]"
                    style={{ border: 'none' }}
                  >
                    <button
                      type="button"
                      className="ql-bold text-base font-semibold text-slate-700"
                      aria-label="Bold"
                    >
                      B
                    </button>
                    <button
                      type="button"
                      className="ql-italic text-base italic text-slate-700"
                      aria-label="Italic"
                    >
                      I
                    </button>
                    <button
                      type="button"
                      className="ql-underline text-base underline text-slate-700"
                      aria-label="Underline"
                    >
                      U
                    </button>
                    <span className="h-6 w-px bg-slate-200" />
                    <button
                      type="button"
                      className="ql-list text-slate-600"
                      value="bullet"
                      aria-label="Bullet list"
                    >
                      &bull;
                    </button>
                    <button
                      type="button"
                      className="ql-align text-slate-600"
                      value=""
                      aria-label="Align left"
                    >
                      &#8801;
                    </button>
                    <button
                      type="button"
                      className="ql-align text-slate-600"
                      value="center"
                      aria-label="Align center"
                    >
                      &#8803;
                    </button>
                    <button
                      type="button"
                      className="ql-align text-slate-600"
                      value="right"
                      aria-label="Align right"
                    >
                      &#8801;
                    </button>
                    <button
                      type="button"
                      className="ql-link text-slate-600"
                      aria-label="Attach link"
                    >
                      &#128279;
                    </button>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      className="rounded-[12px] border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-slate-300 hover:text-slate-900"
                      onClick={() => onToggleNoteEditor(false)}
                      disabled={notePosting}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className={`rounded-[12px] px-4 py-2 text-sm font-semibold text-white transition ${
                        isPostDisabled
                          ? 'bg-slate-200'
                          : 'bg-slate-900 hover:bg-slate-800'
                      } disabled:cursor-not-allowed disabled:opacity-70`}
                      disabled={isPostDisabled}
                      onClick={onPostNote}
                    >
                      {notePosting ? 'Posting...' : 'Post'}
                    </button>
                  </div>
                </div>
              </div>
            ) : null}
            {student.notes.length ? (
              <div className="flex flex-col gap-6">
                {student.notes.map((note) => (
                  <div key={note.id} className="grid grid-cols-[110px_1fr] gap-4">
                    <span className="text-xs font-semibold text-slate-400">
                      {note.dateLabel || formatResourceDate(note.createdAt) || ''}
                    </span>
                    <div className="markdown-data text-sm leading-relaxed font-[14px] font-medium text-[#0F0F0F]">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw]}
                      >
                        {note.content}
                      </ReactMarkdown>
                    </div>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        ) : null}
        {activeTab === 'Shared Resources' ? (
          student.resources.length ? (
            <div className="overflow-hidden">
              <div className="grid grid-cols-[minmax(240px,_2fr)_120px_140px] gap-4  px-4 py-3 text-[14px] font-semibold  text-slate-400">
                <span>Resource Name</span>
                <span>Type</span>
                <span>Added On</span>
              </div>
              <div className="divide-y divide-slate-100">
                {student.resources.map((resource) => {
                  const styles = resourceTypeStyles[resource.type];
                  return (
                    <div
                      key={resource.id}
                      className="grid grid-cols-[minmax(240px,_2fr)_120px_140px] items-center gap-4 px-4 py-4 text-sm text-slate-700"
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`flex h-10 w-10 items-center justify-center rounded-xl ${styles.icon}`}
                        >
                          {resource.type === 'Link' ? (
                           <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <g clip-path="url(#clip0_161_19697)">
    <path d="M8.3335 10.833C8.69137 11.3114 9.14796 11.7073 9.67229 11.9938C10.1966 12.2802 10.7764 12.4506 11.3724 12.4933C11.9683 12.5359 12.5665 12.45 13.1263 12.2411C13.6861 12.0323 14.1944 11.7055 14.6168 11.283L17.1168 8.78298C17.8758 7.99714 18.2958 6.94463 18.2863 5.85214C18.2768 4.75965 17.8386 3.7146 17.0661 2.94207C16.2935 2.16953 15.2485 1.73133 14.156 1.72184C13.0635 1.71234 12.011 2.13232 11.2252 2.89131L9.79183 4.31631" stroke="#2E90FA" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
    <path d="M11.6668 9.16702C11.309 8.68858 10.8524 8.2927 10.328 8.00623C9.80371 7.71977 9.22391 7.54942 8.62796 7.50674C8.03201 7.46406 7.43384 7.55004 6.87405 7.75887C6.31425 7.96769 5.8059 8.29446 5.3835 8.71702L2.8835 11.217C2.12451 12.0029 1.70453 13.0554 1.71402 14.1479C1.72352 15.2403 2.16172 16.2854 2.93426 17.0579C3.70679 17.8305 4.75184 18.2687 5.84433 18.2782C6.93681 18.2877 7.98932 17.8677 8.77517 17.1087L10.2002 15.6837" stroke="#2E90FA" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
  </g>
  <defs>
    <clipPath id="clip0_161_19697">
      <rect width="20" height="20" fill="white"/>
    </clipPath>
  </defs>
</svg>
                          ) : resource.type === 'Image' ? (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M5.83317 3.33301H14.1665C15.5472 3.33301 16.6665 4.45225 16.6665 5.83301V14.1663C16.6665 15.5471 15.5472 16.6663 14.1665 16.6663H5.83317C4.45241 16.6663 3.33317 15.5471 3.33317 14.1663V5.83301C3.33317 4.45225 4.45241 3.33301 5.83317 3.33301Z" stroke="#F79009" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M7.0835 8.74967C7.77385 8.74967 8.3335 8.19003 8.3335 7.49967C8.3335 6.80932 7.77385 6.24967 7.0835 6.24967C6.39314 6.24967 5.8335 6.80932 5.8335 7.49967C5.8335 8.19003 6.39314 8.74967 7.0835 8.74967Z" stroke="#F79009" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M16.6665 12.5L13.3332 9.16699L5.8335 16.667" stroke="#F79009" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                          ) : (
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M12.5002 1.66699H5.00016C4.55814 1.66699 4.13421 1.84259 3.82165 2.15515C3.50909 2.46771 3.3335 2.89163 3.3335 3.33366V16.667C3.3335 17.109 3.50909 17.5329 3.82165 17.8455C4.13421 18.1581 4.55814 18.3337 5.00016 18.3337H15.0002C15.4422 18.3337 15.8661 18.1581 16.1787 17.8455C16.4912 17.5329 16.6668 17.109 16.6668 16.667V5.83366L12.5002 1.66699Z" stroke="#F04438" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M11.6665 1.66699V5.00033C11.6665 5.44235 11.8421 5.86628 12.1547 6.17884C12.4672 6.4914 12.8911 6.66699 13.3332 6.66699H16.6665" stroke="#F04438" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M8.33317 7.5H6.6665" stroke="#F04438" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M13.3332 10.833H6.6665" stroke="#F04438" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
  <path d="M13.3332 14.167H6.6665" stroke="#F04438" stroke-width="1.66667" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
                          )}
                        </div>
                        <div>
                          {resource.type === 'Link' ? (
                            <p className="font-semibold text-slate-900">
                              {resource.name}
                            </p>
                          ) : (
                            <button
                              type="button"
                              className="text-left font-semibold text-slate-900 hover:underline"
                              onClick={() => onOpenResource(resource)}
                            >
                              {resource.name}
                            </button>
                          )}
                          {resource.type === 'Link' && resource.link ? (
                            <a
                              className="text-xs text-slate-400 hover:text-slate-500 hover:underline"
                              href={resource.link}
                              target="_blank"
                              rel="noreferrer"
                            >
                              {resource.link}
                            </a>
                          ) : null}
                        </div>
                      </div>
                      <span
                        className={`w-fit rounded-[10px] px-3 py-1 text-xs font-semibold ${styles.badge}`}
                      >
                        {resource.type}
                      </span>
                      <span className="text-slate-500">
                        {resource.addedOn || '-'}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <p>No shared resources yet.</p>
          )
        ) : null}
      </div>
    </div>
  );
};

export default StudentDetailPanel;
